import React, { useEffect, useMemo, useRef, useState } from "react";
import { highlightTokens, buildSearchHighlightTokens } from "./highlightTokens";
import { requestAskFlash, useAskContext } from "./askContextStore";
import { clearHistorySignal, publishHistorySignal, HISTORY_FOCUS_EVENT, requestBackToMap } from "./historySignalStore";
import { PERIODS, concentration, distributionFromItems, distributionFromSummary, filterItems, formatHsk, learnedNames,
  mergeNames, normalizeName, periodFloor, queryTokens, tariffInfo, treeStatus } from "./historyFunnelModel";

/**
 * 관세사 신고이력 — 깔때기 패널(52차). 검색 결과 탭에서 검색어 모드로만 쓴다(호 모드는 StarHistoryPanel).
 *
 * 1층 검색어 다리: 한글 검색어 → LLM 영문 품명(51차 이벤트) · 이력에서 배운 이름 · 기간 · 수입/수출
 * 2층 세번별 신고 분포: 비율 막대 · 관세율표 품명 · 세율 · 표식(결정트리·현행 표) · 집중도 — 세번 클릭 = 지도 파란 길
 * 3층 선택 세번의 신고품명(중복 제거): 일치율(품명·표준품명·규격·성분 전체에 대고 잼) · 건수 · 최근 · 규격·성분 일치
 * 4층 개별 문서: 품명 클릭 때만 펼침(규격·성분·표준품명·기간). 거래처는 회사 가입 뒤 자사 모드에서만.
 *
 * 분포는 집계 API(`agency-history-summary`)가 있으면 그것(색인당 점수 상위 N건 표본 — 화면이 밝힌다), 없으면 표시된 문서(최대 50)로 만들고 그 사실을 밝힌다.
 * 58차: 서버가 원문 0건에 오타 교정·동의어·영문 철자로 다시 찾으면(`query_rewrite`) 그 사실을 같은 자리에 보이고 LLM 영문 품명 재검색은 하지 않는다. "그대로 보기"는 `rewrite=0`.
 * 숫자는 관행이지 판정이 아니다. 확정은 결정트리만 한다.
 */
const DIRECTION = { import: "수입", export: "수출" };
const ACCENT = "var(--audit-accent)";
const MUTED = "var(--audit-text-muted)";
const BORDER = "1px solid var(--audit-border)";
const TOP_ROWS = 5;
// 2026-09-25 1단계: 이 건수보다 적으면 그 이름의 분포는 우연에 가깝다 — 다음 이름(속성 이름 → 넓은 이름)으로 넘어간다.
export const MIN_HISTORY_DOCS = 20;
const sameName = (a, b) => String(a || "").trim().toUpperCase() === String(b || "").trim().toUpperCase();
const TOP_NAMES = 30;

const number = (v) => (Number.isSafeInteger(v) && v >= 0 ? v.toLocaleString("ko-KR") : "—");
const linkBtn = { border: 0, background: "none", color: ACCENT, cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: 0 };

export default function HistoryFunnelPanel({ searchQuery }) {
  const q0 = String(searchQuery || "").trim();
  const [request, setRequest] = useState({ q: q0 });   // { q, raw?: true } — raw 는 서버 교정 없이 원문 그대로(rewrite=0)
  const [state, setState] = useState({ loading: true });
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("12m");
  // 2026-10-04: 최근 12개월에 신고가 없고 더 옛날에는 있으면(예: NCV4299D1R2G — DHL 2011~2023) 기간을 스스로 넓힌다(12개월 → 5년 → 전체).
  // 사용자가 기간을 직접 고르면 그 뒤로는 넓히지 않는다.
  const [autoWiden, setAutoWiden] = useState(null);   // { from: "12m", to: "5y"|"all" }
  const userPeriod = useRef(false);
  const [summaryKey, setSummaryKey] = useState("");
  const [direction, setDirection] = useState("all");
  const [selected, setSelected] = useState(null);
  const [openName, setOpenName] = useState(null);
  const [details, setDetails] = useState({});
  // 51차: 한글 검색어가 0건이면 제안 신호가 알린 영문 신고 품명으로 한 번 다시 찾는다.
  const [altName, setAltName] = useState(null);
  const [fallback, setFallback] = useState(null);
  const ctx = useAskContext(q0);

  useEffect(() => {
    const on = (e) => { const d = e.detail || {}; if (d.query && d.name_en) setAltName({ query: String(d.query), name_en: String(d.name_en), name_en_generic: String(d.name_en_generic || ""), name_ko: String(d.name_ko || "") }); };
    window.addEventListener("loginhs:proposal-loaded", on);
    return () => window.removeEventListener("loginhs:proposal-loaded", on);
  }, []);
  useEffect(() => { setFallback(null); setSelected(null); setOpenName(null); setRequest({ q: q0 }); setFromMap(null);
    setPeriod("12m"); setAutoWiden(null); userPeriod.current = false; }, [q0]);
  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setState({ loading: true });
    setSummary(null);
    const params = new URLSearchParams({ q: request.q, hs4: "" });
    if (request.raw) params.set("rewrite", "0");
    fetch(`/api/onto/agency-history-extra?${params}`, { signal: controller.signal })
      .then(async (r) => { if (!r.ok) throw new Error("검색 실패"); return r.json(); })
      .then((data) => {
        if (!current) return;
        setState({ data, for: request.q });   // 어느 검색어의 결과인지 — 다음 이름으로 넘어갈지 판단할 때 옛 결과를 보지 않게
        // 55차: 탭 배지는 판단이 되는 숫자 = 이력이 가리키는 세번 수(받은 문서의 서로 다른 세번). 문서 수는 패널 안 각주로만.
        try {
          const received = Array.isArray(data?.items) ? data.items : [];
          const codes = new Set(received.map((it) => String(it?.hsk || "").replace(/\D/g, "")).filter((d) => d.length >= 4));
          const total = Number.isSafeInteger(data?.total) ? data.total : received.length;
          window.dispatchEvent(new CustomEvent("loginhs:history-count", { detail: { q: q0, count: codes.size, documents: total, lowerBound: data?.total_is_lower_bound === true } }));
        } catch (_) { /* ignore */ }
      })
      .catch((error) => { if (current && error.name !== "AbortError") setState({ error: true }); });
    return () => { current = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  // 집계 API(제안 명세 broker_history_summary_v1) — 아직 없으면 조용히 문서 분포로 대신한다(fail-open).
  const from = periodFloor(period);
  useEffect(() => {
    if (!request.q) return undefined;
    const controller = new AbortController();
    let current = true;
    const params = new URLSearchParams({ q: request.q, direction: direction === "all" ? "" : direction, from: from || "" });
    if (request.raw) params.set("rewrite", "0");
    fetch(`/api/onto/agency-history-summary?${params}`, { signal: controller.signal })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((data) => { if (current) { setSummary(distributionFromSummary(data) ? data : null); setSummaryKey(`${request.q}|${from || ""}|${direction}`); } })
      .catch(() => { if (current) { setSummary(null); setSummaryKey(`${request.q}|${from || ""}|${direction}`); } });
    return () => { current = false; controller.abort(); };
  }, [request, direction, from]);
  // 2026-09-25 1단계(실측 docs/audits/2026-09-25-broker-history-attribute-name-measure.json): 이력 품명은 영문이라 한글 검색어는 40개 중 32개가 0건이고,
  // 구분 속성을 뺀 넓은 영문 이름(COFFEE GRINDER)은 다른 물품(전동 8509)을 분모에 섞는다. 그래서
  //   ① 처음 검색어가 MIN_HISTORY_DOCS 미만이면 LLM 이 속성을 살려 읽은 영문 품명(name_en, 예: MANUAL COFFEE GRINDER)으로,
  //   ② 그것도 모자라면 넓은 이름(name_en_generic)으로 — 이때는 '같은 물품이 아닌 신고가 섞일 수 있음'(broad)을 밝히고 지도에 길을 긋지 않는다.
  // 서버 재검색(58차 query_rewrite)보다 LLM 속성 이름을 먼저 쓴다 — 서버 동의어는 속성을 모른다(커피→coffee 로 넓어진다).
  useEffect(() => {
    if (state.loading || state.error || !altName || altName.query !== q0 || state.for !== request.q) return;
    if (fallback && (fallback.manual || fallback.off)) return;
    const total = Number.isSafeInteger(state.data?.total) ? state.data.total : (state.data?.items || []).length;
    const attributed = altName.name_en;
    const generic = altName.name_en_generic;
    if (!fallback && request.q === q0 && !request.raw) {
      if (total >= MIN_HISTORY_DOCS && !state.data?.query_rewrite) return;
      if (attributed && !sameName(attributed, q0)) { setFallback({ from: q0, fromTotal: total, to: attributed, kind: "attributed" }); setRequest({ q: attributed }); return; }
      if (generic && !sameName(generic, q0) && total < MIN_HISTORY_DOCS) { setFallback({ from: q0, fromTotal: total, to: generic, kind: "generic" }); setRequest({ q: generic }); }
      return;
    }
    if (fallback && fallback.kind === "attributed" && sameName(request.q, fallback.to) && total < MIN_HISTORY_DOCS && generic && !sameName(generic, fallback.to)) {
      setFallback({ ...fallback, to: generic, kind: "generic", attributed: fallback.to, attributedTotal: total });
      setRequest({ q: generic });
    }
  }, [altName, state, request, q0, fallback]);

  const items = state.data?.items || [];
  const toks = useMemo(() => queryTokens(request.q), [request.q]);
  const marks = useMemo(() => buildSearchHighlightTokens(request.q), [request.q]);
  const filtered = useMemo(() => filterItems(items, { direction, from }), [items, direction, from]);
  const dist = useMemo(() => distributionFromSummary(summary) || distributionFromItems(filtered), [summary, filtered]);
  const conc = concentration(dist);
  useEffect(() => {
    if (userPeriod.current || period === "all" || direction !== "all" || state.loading || state.error || state.for !== request.q) return;
    if (summaryKey !== `${request.q}|${from || ""}|${direction}`) return;   // 이 기간의 집계가 아직 안 왔다
    if (!items.length || dist.rows.length > 0) return;
    const next = period === "12m" ? "5y" : "all";
    setAutoWiden((w) => ({ from: (w && w.from) || period, to: next }));
    setPeriod(next);
  }, [summaryKey, dist.rows.length, items.length, period, direction, state, request.q, from]);
  const top = dist.rows[0]?.hsk || null;
  const current = selected && dist.rows.some((r) => r.hsk === selected) ? selected : top;
  const [showAll, setShowAll] = useState(false);
  // 지도 배지에서 온 요청 — 분포가 아직 없으면 도착할 때까지 들고 있는다.
  const [focusCode, setFocusCode] = useState(null);
  const [fromMap, setFromMap] = useState(null);   // 지도에서 넘어왔을 때만 '← 지도로 돌아가기' 줄
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");
  const tableRef = useRef(null);
  useEffect(() => {
    const on = (e) => { if (e?.detail?.code) { setFocusCode(e.detail.code); setFromMap(e.detail.code); } };
    window.addEventListener(HISTORY_FOCUS_EVENT, on);
    return () => window.removeEventListener(HISTORY_FOCUS_EVENT, on);
  }, []);
  useEffect(() => {
    if (!focusCode || !dist.rows.length) return;
    const idx = dist.rows.findIndex((r) => String(r.hsk).replace(/\D/g, "").startsWith(focusCode));
    if (idx < 0) { setFocusCode(null); return; }
    const hsk = dist.rows[idx].hsk;
    setSelected(hsk); setOpenName(null);
    if (idx >= TOP_ROWS) setShowAll(true);
    setFocusCode(null);
    setTimeout(() => {
      const row = tableRef.current?.querySelector(`[data-hsk="${hsk}"]`);
      try { row?.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) { /* jsdom */ }
    }, 60);
  }, [focusCode, dist.rows]);
  const visibleRows = showAll ? dist.rows : dist.rows.slice(0, TOP_ROWS);
  const learned = useMemo(() => learnedNames(items, request.q), [items, request.q]);
  const namesSource = useMemo(() => {
    const fromSummary = dist.rows.find((r) => r.hsk === current)?.names;
    if (Array.isArray(fromSummary) && fromSummary.length) return fromSummary;
    return filtered.filter((it) => String(it.hsk || "").replace(/\D/g, "") === current);
  }, [dist, current, filtered]);
  const names = useMemo(() => mergeNames(namesSource, toks).slice(0, TOP_NAMES), [namesSource, toks]);
  // 57차: 지도에 이력 신호를 게시한다 — 세번별 비율·관측과 1위 세번의 "같은 물품" 근거(일치율 100% 관측). 판정이 아니라 근거다.
  useEffect(() => {
    if (!q0 || state.loading || state.error || !dist.rows.length) { if (q0) clearHistorySignal(q0); return undefined; }
    const byCode = Object.fromEntries(dist.rows.map((r) => [r.hsk, { share: r.share, observations: r.observations }]));
    const topRow = dist.rows[0];
    const topDocs = Array.isArray(topRow.names) && topRow.names.length ? topRow.names : filtered.filter((it) => String(it.hsk || "").replace(/\D/g, "") === topRow.hsk);
    const matched = mergeNames(topDocs, toks).filter((n) => n.rate === 100).reduce((s, n) => s + n.observations, 0);
    const secondRow = dist.rows[1] || null;
    publishHistorySignal({ q: q0, top: { hsk: topRow.hsk, share: topRow.share, observations: topRow.observations, matched },
      second: secondRow ? { hsk: secondRow.hsk, share: secondRow.share, observations: secondRow.observations } : null,
      byCode, documents: Number.isSafeInteger(state.data?.total) ? state.data.total : null, partial: dist.partial, sampled: dist.sampled === true, searched: request.q,
      // 넓은 이름으로 찾은 분포 — 같은 물품이 아닌 신고가 섞였을 수 있다. 지도는 배지만 붙이고 길을 긋지 않는다.
      broad: Boolean(fallback && fallback.kind === "generic" && sameName(request.q, fallback.to)) });
    return undefined;
  }, [q0, state, dist, filtered, toks, request.q]);
  useEffect(() => () => clearHistorySignal(q0), [q0]);

  // 관세율표 상세(세율·품명·현행 여부) — 분포 상위 호만, 호당 1회, fail-open.
  useEffect(() => {
    const hs4s = [...new Set(dist.rows.slice(0, TOP_ROWS).map((r) => r.hs4))].filter((h) => /^\d{4}$/.test(h) && !(h in details));
    if (!hs4s.length) return undefined;
    let current = true;
    hs4s.forEach((hs4) => {
      fetch(`/api/onto/heading-detail?hs=${hs4}&lang=kor`)
        .then(async (r) => (r.ok ? r.json() : null))
        .then((data) => { if (current) setDetails((prev) => (hs4 in prev ? prev : { ...prev, [hs4]: Array.isArray(data?.rows) ? data : null })); })
        .catch(() => { if (current) setDetails((prev) => (hs4 in prev ? prev : { ...prev, [hs4]: null })); });
    });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dist]);

  const pickHsk = (hsk) => { setSelected(hsk); setOpenName(null); requestAskFlash(q0, hsk); };
  const searchWith = (name) => { setFallback({ from: q0, to: name, manual: true }); setRequest({ q: name }); };
  const resetQuery = () => { setFallback({ from: q0, to: "", off: true }); setRequest({ q: q0 }); };
  // 58차: 서버 재검색 안내 — 응답 `query_rewrite{method, from, to}`. 그대로 보기는 같은 검색어를 rewrite=0 으로.
  const rewrite = !request.raw && state.data?.query_rewrite && state.data.query_rewrite.to ? state.data.query_rewrite : null;
  const REWRITE_METHOD = { typo_ko: "한글 오타 교정", synonym: "동의어", spelling: "이력 영문 철자 제안" };
  const resetRaw = () => { setFallback(null); setRequest({ q: q0, raw: true }); };
  const unfilteredEmpty = items.length === 0;
  const filteredEmpty = !unfilteredEmpty && filtered.length === 0 && !summary;
  const td = { padding: "8px 8px", borderTop: BORDER, verticalAlign: "top", overflowWrap: "anywhere" };
  const th = { padding: "5px 8px", textAlign: "left", fontWeight: 400, color: MUTED, fontSize: 11 };
  // 53차: 파랑은 선택된 세번 한 곳(글자·막대)에만. 나머지는 검정·회색 농도로 층을 낸다(칩·상자·배지 없음).
  const word = (active, onClick, label, pressed) => (
    <button key={label} type="button" aria-pressed={pressed} onClick={onClick}
      style={{ border: 0, background: "none", padding: "0 2px", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
        color: active ? "var(--audit-text)" : MUTED, fontWeight: active ? 700 : 400 }}>{label}</button>
  );
  const sep = <span aria-hidden="true" style={{ color: "var(--audit-text-faint)" }}>·</span>;
  const nameWord = (label, tag, active, onClick, title) => (
    <span key={label} style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
      <button type="button" onClick={onClick} title={title}
        style={{ border: 0, background: "none", padding: "0 1px", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
          color: active ? "var(--audit-text)" : "var(--audit-text-muted)", fontWeight: active ? 700 : 400,
          borderBottom: active ? "2px solid var(--audit-text)" : "2px solid transparent" }}>{label} <span style={{ fontSize: 10, fontWeight: 400, color: MUTED }}>{tag}</span></button>
    </span>
  );
  const stat = (label, value, key) => (
    <span key={key || label} style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: "var(--audit-text)" }}>{value}</span>{" "}
      <span style={{ color: MUTED }}>{label}</span>
    </span>
  );

  return <section aria-label="관세사 신고이력" style={{ marginBottom: 22, fontSize: 12 }}>
    <h3 style={{ fontSize: 14, margin: "0 0 4px" }}>관세사 신고이력 · 신고 분포</h3>
    <p style={{ color: MUTED, margin: "0 0 12px" }}>
      익명 이력 {state.data?.source_rows ? `원본 ${number(state.data.source_rows)}행` : ""} · 신고비율은 관행이며 법적 확정이 아닙니다 · 세번은 규격·성분을 보고 판단합니다 · 타사 이력은 분포·품명까지, 자사 상세(거래처·개별 신고)는 회사 가입 뒤 자사 모드에서 봅니다
    </p>

    {/* 1층 · 검색어 다리 — 현재 이름은 검은 밑줄, 후보 이름은 회색 글자 */}
    {fromMap && (
      <div data-testid="history-back-to-map" role="status"
        style={{ position: "sticky", top: 0, zIndex: 2, display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 12px", padding: "7px 10px", marginBottom: 10, background: "var(--audit-bg-elevated, #fff)", border: "1px solid var(--audit-border)", borderLeft: "3px solid var(--audit-accent, #2563eb)", borderRadius: 6 }}>
        <button type="button" onClick={() => { setFromMap(null); requestBackToMap(); }}
          style={{ ...linkBtn, fontWeight: 700, color: "var(--audit-accent, #2563eb)" }}>← 지도로 돌아가기</button>
        <span style={{ color: MUTED }}>
          지도에서 고른 {formatHsk(fromMap)}의 신고 내역입니다 · 단축키 <kbd style={{ fontFamily: "inherit", border: "1px solid var(--audit-border)", borderBottomWidth: 2, borderRadius: 4, padding: "0 5px", fontSize: 11.5 }}>{isMac ? "⌘ + [" : "Alt + ←"}</kbd> 로도 돌아갑니다
        </span>
        <button type="button" aria-label="이 안내 닫기" onClick={() => setFromMap(null)} style={{ ...linkBtn, marginLeft: "auto", color: MUTED }}>✕</button>
      </div>
    )}
    <div data-testid="history-bridge" style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", alignItems: "baseline", marginBottom: 4 }}>
      <span style={{ color: MUTED }}>검색어</span>
      <b>{q0 || "—"}</b>
      {(fallback && fallback.to) || altName?.query === q0 || learned.length > 0 ? <span aria-hidden="true" style={{ color: MUTED }}>→</span> : null}
      {altName && altName.query === q0 && altName.name_en && nameWord(altName.name_en, "LLM", request.q === altName.name_en, () => searchWith(altName.name_en), "LLM이 읽은 영문 신고 품명 · 이름일 뿐 세번이 아닙니다")}
      {learned.filter((n) => n !== (altName?.name_en || "").toUpperCase() && n !== request.q.toUpperCase()).map((n) => nameWord(n, "이력", false, () => searchWith(n), "이력에서 함께 나온 이름"))}
      <span style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", gap: 5, alignItems: "baseline" }}>{PERIODS.flatMap((p, i) => [i > 0 && <span key={`s${p.key}`}>{sep}</span>, word(period === p.key, () => { userPeriod.current = true; setAutoWiden(null); setPeriod(p.key); }, p.label, period === p.key)])}</span>
        <span style={{ display: "inline-flex", gap: 5, alignItems: "baseline" }}>{[["all", "전체"], ["import", "수입"], ["export", "수출"]].flatMap(([k, label], i) => [i > 0 && <span key={`s${k}`}>{sep}</span>, word(direction === k, () => setDirection(k), label, direction === k)])}</span>
      </span>
    </div>
    {rewrite && (
      <div data-testid="history-query-rewrite" style={{ color: MUTED, marginBottom: 12 }}>
        <b style={{ color: "var(--audit-text)" }}>{rewrite.from || request.q}</b>은(는) 신고이력에 0건이라 {REWRITE_METHOD[rewrite.method] || "교정"}으로 <b style={{ color: "var(--audit-text)" }}>{rewrite.to}</b>(으)로 다시 찾았습니다 · 이름은 교정일 뿐 세번이 아닙니다 ·{" "}
        <button type="button" style={{ ...linkBtn, color: MUTED, textDecoration: "underline" }} onClick={resetRaw}>처음 검색어 그대로 보기</button>
      </div>
    )}
    {!rewrite && fallback && fallback.to && !fallback.off && (
      <div data-testid="history-english-fallback" style={{ color: MUTED, marginBottom: 12 }}>
        {fallback.manual ? <>{fallback.to}(으)로 찾는 중 · </>
          : fallback.kind === "generic" ? <><b style={{ color: "var(--audit-text)" }}>{fallback.attributed || fallback.from}</b>은(는) 신고이력이 {fallback.attributedTotal ?? fallback.fromTotal ?? 0}건뿐이라 넓은 영문 품명 <b style={{ color: "var(--audit-text)" }}>{fallback.to}</b>(으)로 찾았습니다 · 같은 물품이 아닌 신고가 섞였을 수 있어 지도에 길을 긋지 않습니다 · </>
          : <><b style={{ color: "var(--audit-text)" }}>{fallback.from}</b>은(는) 신고이력이 {fallback.fromTotal ?? 0}건이라 LLM이 속성을 살려 읽은 영문 신고 품명 <b style={{ color: "var(--audit-text)" }}>{fallback.to}</b>(으)로 다시 찾았습니다 · 이름은 제안일 뿐 세번이 아닙니다 · </>}
        <button type="button" style={{ ...linkBtn, color: MUTED, textDecoration: "underline" }} onClick={resetQuery}>처음 검색어 그대로 보기</button>
      </div>
    )}

    {state.loading ? <p role="status">관세사 이력 검색 중…</p>
      : state.error ? <p role="alert">이력 검색에 연결하지 못했습니다. 다시 검색해 주세요.</p>
      : unfilteredEmpty ? <p>이 검색 대상에서 검색된 후보가 없습니다. 실제 신고이력 부재를 뜻하지 않습니다.{/[가-힣]/.test(request.q) && " 이력 품명은 대부분 영문이라 영문 품명으로 다시 검색해 보세요."}</p>
      : <>
        {autoWiden && !userPeriod.current && (
          <div data-testid="history-auto-widen" style={{ color: MUTED, marginTop: 8 }}>
            {PERIODS.find((p) => p.key === autoWiden.from)?.label}에는 신고가 없어 <b style={{ color: "var(--audit-text)" }}>{PERIODS.find((p) => p.key === autoWiden.to)?.label}</b>(으)로 넓혀 보입니다 · 오래된 신고는 지금 관행과 다를 수 있습니다
          </div>
        )}
        {/* 요약 — 상자 없이 큰 숫자 한 줄 */}
        <div data-testid="history-summary" style={{ display: "flex", flexWrap: "wrap", gap: "6px 26px", margin: "10px 0 16px" }}>
          {stat(dist.partial ? "신고 관측 · 표시분" : dist.sampled ? "신고 관측 · 표본" : "신고 관측", number(dist.total))}
          {stat(conc.kind === "single" ? "한 세번 집중" : conc.kind === "split" ? "둘로 갈림" : conc.kind === "spread" ? "흩어짐 · 최다" : "분포 없음",
            conc.kind === "single" ? `${Math.round(conc.topShare)}%` : conc.kind === "split" ? conc.label.replace("둘로 갈림 ", "") : conc.kind === "spread" ? `${Math.round(conc.topShare)}%` : "—", "conc")}
          {stat("세번", dist.rows.length)}
          {ctx && current && (treeStatus(current, ctx) === "live"
            ? <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}><span style={{ color: MUTED }}>결정트리</span>{" "}<span style={{ fontSize: 18, fontWeight: 700 }}>{formatHsk(current)}</span>{" "}<span style={{ color: MUTED }}>살아 있음</span></span>
            : <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}><span style={{ color: MUTED }}>결정트리</span>{" "}<span style={{ fontSize: 18, fontWeight: 700 }}>{formatHsk(current)}</span>{" "}<span style={{ color: MUTED }}>트리 밖</span></span>)}
        </div>
        {filteredEmpty ? <p>{PERIODS.find((p) => p.key === period)?.label}·{direction === "all" ? "전체" : DIRECTION[direction]} 조건에는 표시된 문서가 없습니다. <button type="button" style={linkBtn} onClick={() => { userPeriod.current = true; setAutoWiden(null); setPeriod("all"); setDirection("all"); }}>전체 기간·전체 방향으로 보기</button></p> : null}

        {/* 2층 · 세번별 신고 분포 — 파랑은 선택 행의 세번·막대에만, 선택 행은 왼쪽 검은 선 */}
        {dist.rows.length > 0 && <div style={{ marginBottom: 18 }}>
          <div style={{ marginBottom: 2 }}>
            <b>세번별 신고 분포</b>
            <span style={{ color: MUTED, marginLeft: 8 }}>{dist.partial
              ? `이력 문서 ${number(state.data?.total)}${state.data?.total_is_lower_bound ? "+" : ""}개(전체 기간) 중 상위 ${items.length}개 수신 · 그중 ${filtered.length}개 표시 기준 · 집계 API 연결 전이라 전체 분포가 아닙니다`
              : dist.sampled
                ? `검색 점수 상위 ${number(dist.sampleDocuments)}건${dist.samplePerIndex ? `(색인당 ${number(dist.samplePerIndex)}건)` : ""} 표본의 분포 · 전체 일치 문서 ${number(dist.totalDocuments)}${dist.totalLowerBound ? "+" : ""}개 · 표본이라 전체 분포와 다를 수 있습니다`
                : "전체 이력 집계"} · 세번을 누르면 지도에 그 세번의 길이 켜집니다</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table ref={tableRef} data-testid="history-distribution" style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead><tr>{["세번", "신고비율", "관측", "관세율표 품명", "세율", "최근", "표식"].map((h, i) => <th key={h} style={{ ...th, paddingLeft: i === 0 ? 10 : 8 }}>{h}</th>)}</tr></thead>
              <tbody>{visibleRows.map((r) => {
                const info = tariffInfo(r.hsk, details[r.hs4]);
                const detailKnown = details[r.hs4] !== undefined && details[r.hs4] !== null;
                const tree = treeStatus(r.hsk, ctx);
                const active = r.hsk === current;
                const tone = active ? "var(--audit-text)" : "var(--audit-text-muted)";
                return <tr key={r.hsk} data-testid="history-hs-row" data-hsk={r.hsk} data-active={active ? "1" : "0"} style={{ color: tone, boxShadow: active ? "inset 3px 0 0 var(--audit-text)" : "none" }}>
                  <td style={{ ...td, paddingLeft: 10 }}><button type="button" onClick={() => pickHsk(r.hsk)} style={{ ...linkBtn, fontFamily: "ui-monospace, monospace", color: active ? ACCENT : tone, fontWeight: active ? 700 : 400 }}>{formatHsk(r.hsk)}</button></td>
                  <td style={{ ...td, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: "var(--audit-bg-subtle)", borderRadius: 3 }}><div style={{ width: `${Math.max(1, r.share)}%`, height: 6, background: active ? ACCENT : "var(--audit-text-faint)", borderRadius: 3 }} /></div>
                      <span style={{ width: 48, textAlign: "right", fontWeight: active ? 700 : 400 }}>{r.share}%</span>
                    </div>
                  </td>
                  <td style={td}>{number(r.observations)}</td>
                  <td style={td}>{info.name || (detailKnown ? <span style={{ color: MUTED }}>현행 관세율표에 없음</span> : "—")}</td>
                  <td style={td}>{info.basic != null ? <>{info.basic}%{info.ftaMin != null && <span style={{ color: MUTED }}> FTA {info.ftaMin}%</span>}</> : "—"}</td>
                  <td style={td}>{r.last || "—"}</td>
                  <td style={td}>
                    {tree === "live" && <span>● 트리 살아 있음</span>}
                    {tree === "outside" && <span style={{ color: MUTED }}>○ 트리 밖</span>}
                    {detailKnown && !info.found && <span style={{ color: MUTED, marginLeft: tree ? 6 : 0 }}>만료 가능</span>}
                  </td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          {dist.rows.length > TOP_ROWS && <div style={{ padding: "5px 10px", borderTop: BORDER }}>
            <button type="button" style={{ ...linkBtn, color: MUTED, textDecoration: "underline" }} onClick={() => setShowAll((v) => !v)}>{showAll ? "상위 5개만 보기" : `그 밖 ${dist.rows.length - TOP_ROWS}개 세번 펼치기`}</button>
          </div>}
        </div>}

        {/* 3층 · 선택 세번의 신고품명 — 일치율은 숫자 굵기로만, 낮은 행은 통째로 옅게 */}
        {current && <div>
          <div style={{ marginBottom: 2 }}>
            <b>{formatHsk(current)} 신고품명</b>
            <span style={{ color: MUTED, marginLeft: 8 }}>중복 제거 {names.length}개 · 일치율 순 · 규격·성분 일치 먼저 · 품명을 누르면 규격·성분·기간이 펼쳐집니다</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="history-names" style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead><tr>{["일치율", "거래품명 · 규격·성분", "구분", "관측", "최근"].map((h, i) => <th key={h} style={{ ...th, paddingLeft: i === 0 ? 10 : 8 }}>{h}</th>)}</tr></thead>
              <tbody>{names.map((n) => {
                const open = openName === n.key;
                const tone = n.rate == null || n.rate >= 60 ? "var(--audit-text)" : "var(--audit-text-muted)";
                return <React.Fragment key={n.key}>
                  <tr data-testid="history-name-row" style={{ color: tone }}>
                    <td style={{ ...td, paddingLeft: 10 }}>{n.rate == null ? "—"
                      : <span data-testid="history-match-rate" style={{ fontWeight: n.rate >= 90 ? 700 : 400, color: n.rate >= 90 ? "var(--audit-text)" : tone }}>{n.rate}%</span>}</td>
                    <td style={td}>
                      <button type="button" style={{ ...linkBtn, color: "inherit", textAlign: "left" }} onClick={() => setOpenName(open ? null : n.key)}>{highlightTokens(n.name, marks)}</button>
                      {n.standard_name && normalizeName(n.standard_name) !== n.key && <span style={{ marginLeft: 6, fontSize: 11, color: MUTED }}>표준 {n.standard_name}</span>}
                      {n.specHit && <span title="검색어가 규격·성분에서도 맞았습니다" style={{ marginLeft: 6, fontSize: 11, color: MUTED }}>규격·성분 ✓</span>}
                      {/* 54차: 거래품명은 담당자가 세관이 알 수 있는 말로 정리한 줄, 규격·성분은 인보이스 원문 — 둘 다 보인다(규격은 둘째 줄, 전체는 클릭). */}
                      {(n.spec || n.composition) && <div data-testid="history-name-spec" style={{ fontSize: 11, color: MUTED, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 520 }}>
                        {n.spec ? highlightTokens(n.spec, marks) : ""}{n.spec && n.composition ? " · " : ""}{n.composition ? highlightTokens(n.composition, marks) : ""}
                      </div>}
                    </td>
                    <td style={td}>{n.directions.map((d) => DIRECTION[d] || d).join("·") || "—"}</td>
                    <td style={td}>{number(n.observations)}</td>
                    <td style={td}>{n.last || "—"}</td>
                  </tr>
                  {open && <tr data-testid="history-name-detail"><td colSpan={5} style={{ ...td, paddingLeft: 10, background: "var(--audit-bg-subtle)", color: "var(--audit-text)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "4px 16px" }}>
                      <span><span style={{ color: MUTED }}>모델·규격</span> {n.spec ? highlightTokens(n.spec, marks) : "—"}</span>
                      <span><span style={{ color: MUTED }}>성분</span> {n.composition ? highlightTokens(n.composition, marks) : "—"}</span>
                      <span><span style={{ color: MUTED }}>표준품명</span> {n.standard_name || "—"}</span>
                      <span><span style={{ color: MUTED }}>기간</span> {n.first || "—"} ~ {n.last || "—"}</span>
                      <span><span style={{ color: MUTED }}>문서</span> {n.entries.length}개 · 관측 {number(n.observations)} (독립 신고 수 아님)</span>
                      <span style={{ color: MUTED }}>거래처·신고번호는 자사 모드(회사 가입 뒤)에서만 보입니다</span>
                    </div>
                  </td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table>
          </div>
        </div>}
      </>}
  </section>;
}
